import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Paper,
    Alert,
    Chip
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { ArrowBack, Add, Delete, Save, Analytics, Visibility } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { ChangeEvent, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { urls } from "@routes";
import { QuestionType, Survey, SurveyQuestion } from "@admin/types/survey.types";

interface PageData {
    status_code: number;
    status_text: string;
    questions: SurveyQuestion[];
}

const QUESTION_TYPES: { value: QuestionType; label: string; description: string }[] = [
    { value: "dropdown", label: "Dropdown", description: "Single choice from a dropdown menu" },
    { value: "radio", label: "Radio Buttons", description: "Single choice with radio buttons" },
    { value: "checkbox", label: "Checkboxes", description: "Multiple choice with checkboxes" },
    { value: "scale", label: "Scale", description: "Rating scale (1-10)" },
    { value: "text", label: "Short Text", description: "Single line text input" },
    { value: "textarea", label: "Long Text", description: "Multi-line text input" },
    { value: "attachment", label: "File Attachment", description: "Image/screenshot upload (.jpeg, .png, etc.)" },
];

export default function AdminEditSurveyQuestion() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { questionUid } = useParams<{ questionUid: string }>();

    const [pageData, setPageData] = useState<PageData>();
    const [currentQuestion, setCurrentQuestion] = useState<SurveyQuestion | null>(null);
    const [surveysData, setSurveysData] = useState<{ surveys: Survey[] }>();

    // Form state
    const [surveyUid, setSurveyUid] = useState("");
    const [questionText, setQuestionText] = useState("");
    const [questionType, setQuestionType] = useState<QuestionType>("dropdown");
    const [options, setOptions] = useState<string[]>(["", ""]);
    const [displayOrder, setDisplayOrder] = useState("0");
    const [isRequired, setIsRequired] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [placeholderText, setPlaceholderText] = useState("");
    const [helpText, setHelpText] = useState("");
    const [validationRules, setValidationRules] = useState("");

    // Query for fetching surveys
    const surveysQuery = useQuery({
        queryKey: ["surveysForQuestionEdit"],
        queryFn: () => authenticateAndFetchData("/admin/surveys/"),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (surveysQuery.data) {
            setSurveysData(surveysQuery.data.data);
        }
    }, [surveysQuery.data]);

    // Query for fetching question data
    const pageDataQuery = useQuery({
        queryKey: ["adminEditSurveyQuestionPageData", questionUid],
        queryFn: () => authenticateAndFetchData("/admin/survey/questions/"),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (pageDataQuery.data) {
            setPageData(pageDataQuery.data.data);

            // Find the specific question
            const question = pageDataQuery.data.data.questions.find(
                (q: SurveyQuestion) => q.uid === questionUid
            );

            if (question) {
                setCurrentQuestion(question);
                // Populate form fields
                setSurveyUid(question.survey_uid || "");
                setQuestionText(question.question_text);
                setQuestionType(question.question_type);

                // Extract choices from options object
                if (question.options && question.options.choices) {
                    setOptions(question.options.choices);
                } 
                else if (question.options) {
                    setOptions(question.options);
                }
                else {
                    setOptions(["", ""]);
                }

                setDisplayOrder(question.display_order.toString());
                setIsRequired(question.is_required);
                setIsActive(question.is_active);
                setPlaceholderText(question.placeholder_text || "");
                setHelpText(question.help_text || "");
                setValidationRules(
                    question.validation_rules ? JSON.stringify(question.validation_rules, null, 2) : ""
                );
            }
        }
    }, [pageDataQuery.data, questionUid]);

    // Mutation for updating question
    const updateQuestionMutation = useMutation({
        mutationKey: ["updateSurveyQuestion"],
        mutationFn: () => {
            const payload: any = {
                question_uid: questionUid,
                survey_uid: surveyUid,
                question_text: questionText,
                question_type: questionType,
                display_order: parseInt(displayOrder) || 0,
                is_required: isRequired,
                is_active: isActive,
            };

            // Add options for dropdown, radio, checkbox
            if (["dropdown", "radio", "checkbox"].includes(questionType)) {
                payload.options = options.filter(opt => opt.trim() !== "");
            }

            // Add placeholder and help text
            payload.placeholder_text = placeholderText.trim() || null;
            payload.help_text = helpText.trim() || null;

            // Add validation rules if provided
            if (validationRules.trim()) {
                try {
                    payload.validation_rules = JSON.parse(validationRules);
                } catch (e) {
                    // Will be caught in validation
                }
            } else {
                payload.validation_rules = null;
            }

            return authenticateAndPostData("/admin/survey/questions/update/", payload);
        },
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            enqueueSnackbar("Survey question updated successfully", { variant: "success" });
            navigate(urls["adminSurveyQuestions"]);
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message || "Failed to update question", {
                variant: "error",
            });
        }
    });

    // Handlers
    function displayOrderHandler(event: ChangeEvent<HTMLInputElement>) {
        const newValue = event.target.value;
        if (newValue === "" || /^[0-9\b]+$/.test(newValue)) {
            setDisplayOrder(newValue);
        }
    }

    function handleAddOption() {
        setOptions([...options, ""]);
    }

    function handleRemoveOption(index: number) {
        if (options.length > 2) {
            setOptions(options.filter((_, i) => i !== index));
        }
    }

    function handleOptionChange(index: number, value: string) {
        const newOptions = [...options];
        newOptions[index] = value;
        setOptions(newOptions);
    }

    function validateForm(): boolean {
        // Survey is required
        if (!surveyUid) {
            enqueueSnackbar("Please select a survey", { variant: "error" });
            return false;
        }

        // Question text is required
        if (!questionText.trim()) {
            enqueueSnackbar("Question text is required", { variant: "error" });
            return false;
        }

        // For dropdown, radio, checkbox - need at least 2 options
        if (["dropdown", "radio", "checkbox"].includes(questionType)) {
            const validOptions = options.filter(opt => opt.trim() !== "");
            if (validOptions.length < 2) {
                enqueueSnackbar("At least 2 options are required for this question type", { variant: "error" });
                return false;
            }
        }

        // Validate JSON if validation rules provided
        if (validationRules.trim()) {
            try {
                JSON.parse(validationRules);
            } catch (e) {
                enqueueSnackbar("Validation rules must be valid JSON", { variant: "error" });
                return false;
            }
        }

        return true;
    }

    function handleSubmit() {
        if (validateForm()) {
            updateQuestionMutation.mutate();
        }
    }

    // Show options input only for certain question types
    const showOptionsInput = ["dropdown", "radio", "checkbox"].includes(questionType);

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    if (pageDataQuery.isLoading) {
        return <PageLoading />;
    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />;
    } else if (!currentQuestion) {
        return (
            <Stack direction="column" spacing={2} sx={{ p: 4 }}>
                <Typography variant="h6">Question not found</Typography>
                <Button variant="contained" onClick={() => navigate(urls["adminSurveyQuestions"])}>
                    Back to Questions
                </Button>
            </Stack>
        );
    } else if (pageData && currentQuestion && surveysData) {
        return (
            <Stack direction="column" spacing={3} sx={{ pb: 4, maxWidth: "900px" }}>
                {/* Header */}
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate(urls["adminSurveyQuestions"])}>
                        <ArrowBack />
                    </IconButton>
                    <Box flexGrow={1}>
                        <Typography fontWeight="bold" variant="h5">
                            Edit Survey Question
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Modify existing survey question
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => navigate(urls["adminSurveyResponses"] + `?question_uid=${questionUid}`)}
                        >
                            View Responses
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Analytics />}
                            onClick={() => navigate(urls["adminSurveyAnalytics"].replace(":surveyUid", surveyUid!))}
                        >
                            Analytics
                        </Button>
                    </Stack>
                </Box>

                <Divider />

                {/* Metadata */}
                <Paper sx={{ p: 2, bgcolor: "background.default" }}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip label={`UID: ${currentQuestion.uid}`} size="small" variant="outlined" />
                        <Chip
                            label={`Created: ${new Date(currentQuestion.created_on).toLocaleDateString()}`}
                            size="small"
                            variant="outlined"
                        />
                        <Chip
                            label={currentQuestion.is_active ? "Active" : "Inactive"}
                            size="small"
                            color={currentQuestion.is_active ? "success" : "error"}
                        />
                    </Stack>
                </Paper>

                <Paper sx={{ p: 3 }}>
                    <Stack direction="column" spacing={3}>
                        {/* Survey Selection */}
                        <FormControl fullWidth required>
                            <InputLabel>Survey</InputLabel>
                            <Select
                                value={surveyUid}
                                label="Survey"
                                onChange={(e) => setSurveyUid(e.target.value)}
                            >
                                {surveysData.surveys.map((survey) => (
                                    <MenuItem key={survey.uid} value={survey.uid} disabled={!survey.is_active}>
                                        {survey.title} {!survey.is_active && "(Inactive)"}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>Select which survey this question belongs to</FormHelperText>
                        </FormControl>

                        {/* Question Text */}
                        <TextField
                            label="Question Text"
                            placeholder="e.g., Where did you hear about us?"
                            value={questionText}
                            onChange={(e) => setQuestionText(e.target.value)}
                            multiline
                            rows={2}
                            required
                            fullWidth
                            helperText="The question that will be shown to users"
                        />

                        {/* Question Type */}
                        <FormControl fullWidth required>
                            <InputLabel>Question Type</InputLabel>
                            <Select
                                value={questionType}
                                label="Question Type"
                                onChange={(e) => setQuestionType(e.target.value as QuestionType)}
                            >
                                {QUESTION_TYPES.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        <Box>
                                            <Typography variant="body1">{type.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {type.description}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>Select the type of input field for this question</FormHelperText>
                        </FormControl>

                        {/* Options (for dropdown, radio, checkbox) */}
                        {showOptionsInput && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Answer Options *
                                </Typography>
                                <Stack direction="column" spacing={1.5}>
                                    {options.map((option, index) => (
                                        <Box key={index} display="flex" gap={1} alignItems="center">
                                            <TextField
                                                size="small"
                                                placeholder={`Option ${index + 1}`}
                                                value={option}
                                                onChange={(e) => handleOptionChange(index, e.target.value)}
                                                fullWidth
                                            />
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => handleRemoveOption(index)}
                                                disabled={options.length <= 2}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        startIcon={<Add />}
                                        onClick={handleAddOption}
                                        variant="outlined"
                                        size="small"
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Add Option
                                    </Button>
                                </Stack>
                                <FormHelperText>At least 2 options are required</FormHelperText>
                            </Box>
                        )}

                        {/* Display Order */}
                        <TextField
                            label="Display Order"
                            placeholder="0"
                            value={displayOrder}
                            onChange={displayOrderHandler}
                            type="text"
                            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                            helperText="Lower numbers appear first (0 = highest priority)"
                            fullWidth
                        />

                        <Divider />

                        {/* Checkboxes */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Question Settings
                            </Typography>
                            <Stack direction="column" spacing={1}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isRequired}
                                            onChange={(e) => setIsRequired(e.target.checked)}
                                        />
                                    }
                                    label="Required Question"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isActive}
                                            onChange={(e) => setIsActive(e.target.checked)}
                                        />
                                    }
                                    label="Active (visible to users)"
                                />
                            </Stack>
                        </Box>

                        <Divider />

                        {/* Optional Fields */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Additional Settings (Optional)
                            </Typography>
                            <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
                                <TextField
                                    label="Placeholder Text"
                                    placeholder="e.g., Select an option..."
                                    value={placeholderText}
                                    onChange={(e) => setPlaceholderText(e.target.value)}
                                    fullWidth
                                    helperText="Hint text shown in the input field"
                                />
                                <TextField
                                    label="Help Text"
                                    placeholder="Additional context or instructions"
                                    value={helpText}
                                    onChange={(e) => setHelpText(e.target.value)}
                                    multiline
                                    rows={2}
                                    fullWidth
                                    helperText="Additional help text shown below the question"
                                />
                                <TextField
                                    label="Validation Rules (JSON)"
                                    placeholder='{"min_length": 10, "max_length": 500}'
                                    value={validationRules}
                                    onChange={(e) => setValidationRules(e.target.value)}
                                    multiline
                                    rows={2}
                                    fullWidth
                                    helperText="Advanced: Custom validation rules in JSON format"
                                />
                            </Stack>
                        </Box>

                        {/* Submit Button */}
                        <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(urls["adminSurveyQuestions"])}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Save />}
                                onClick={handleSubmit}
                                disabled={updateQuestionMutation.isPending}
                            >
                                {updateQuestionMutation.isPending ? "Updating..." : "Update Question"}
                            </Button>
                        </Box>
                    </Stack>
                </Paper>
            </Stack>
        );
    }

    return null;
}
